import { Link } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { useStrategyHistory } from '@/lib/useStrategy'

const STATUS_STYLE: Record<string, { dot: string; label: string }> = {
  draft: { dot: 'bg-faint', label: 'DRAFT' },
  active: { dot: 'bg-gain', label: 'ACTIVE' },
  archived: { dot: 'bg-muted', label: 'ARCHIVED' },
}

export function StrategyHistoryPage() {
  const query = useStrategyHistory()
  const items = query.data?.items ?? []

  return (
    <AppShell>
      <article className="fade-in max-w-3xl">
        <header className="flex flex-col items-stretch gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
          <div>
            <p className="tracked-caps text-[10px] text-faint">Strategy</p>
            <h2 className="mt-2 text-2xl text-ink">Versions-Historie</h2>
            <p className="mt-1 font-mono text-xs text-muted">
              {items.length} Version{items.length === 1 ? '' : 'en'}
            </p>
          </div>
          <Link
            to="/strategy"
            className="self-end font-mono text-[10px] text-muted transition-colors hover:text-bernstein md:self-auto"
          >
            ← aktuelle
          </Link>
        </header>

        <div className="mt-10">
          {query.isLoading ? (
            <p className="font-mono text-sm text-muted">Lade Historie …</p>
          ) : query.isError ? (
            <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
              <p className="tracked-caps text-[10px] text-loss">Fehler</p>
              <p className="mt-2 font-mono text-sm text-ink">
                Historie konnte nicht geladen werden.
              </p>
              <p className="mt-1 font-mono text-xs text-muted">{(query.error as Error).message}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="border border-hairline bg-panel px-6 py-12 text-center">
              <p className="tracked-caps text-[10px] text-faint">Leer</p>
              <p className="mt-3 text-sm text-muted">
                Noch keine Strategy angelegt.{' '}
                <Link to="/strategy" className="text-bernstein hover:underline">
                  v1.0 erzeugen
                </Link>
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              {items.map((s) => {
                const style = STATUS_STYLE[s.status] ?? STATUS_STYLE.draft
                return (
                  <div key={s.id} className="hairline-b flex items-baseline gap-3 py-4 md:gap-4">
                    <span
                      className={`mt-2 size-1.5 shrink-0 rounded-full ${style.dot}`}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <p className="text-sm text-ink">v{s.version}</p>
                        <span className="tracked-caps text-[10px] text-muted">{style.label}</span>
                        {s.parent_version_id !== null && (
                          <span className="font-mono text-[10px] text-faint">
                            Fork von #{s.parent_version_id}
                          </span>
                        )}
                      </div>
                      {s.reason && (
                        <p className="mt-1 line-clamp-2 font-mono text-xs text-muted">{s.reason}</p>
                      )}
                      <p className="mt-1 font-mono text-[10px] text-faint md:hidden">
                        {new Date(s.created_at).toLocaleDateString('de-DE')}
                        {s.activated_at &&
                          ` · aktiv seit ${new Date(s.activated_at).toLocaleDateString('de-DE')}`}
                      </p>
                    </div>
                    <p className="hidden font-mono text-[10px] text-faint md:block">
                      {new Date(s.created_at).toLocaleDateString('de-DE')}
                      {s.activated_at &&
                        ` · aktiv seit ${new Date(s.activated_at).toLocaleDateString('de-DE')}`}
                    </p>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </article>
    </AppShell>
  )
}
